import React from 'react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { MFG_COST_SHEET_STAGES, stageStateMeta } from '@/lib/constants';
import { WORKFLOW_CHECKS as checks } from '@/lib/costSheetPending';


function Observation({ value, disabled, save, label }) {
  const [draft, setDraft] = React.useState(value || '');
  React.useEffect(() => setDraft(value || ''), [value]);
  return <textarea aria-label={`Observaciones: ${label}`} placeholder="Observaciones del check" rows={2}
    className="mt-1 w-full rounded border p-2 text-xs" value={draft} disabled={disabled}
    onChange={(event) => setDraft(event.target.value)} onBlur={() => { if (draft !== (value || '')) save(draft); }} />;
}

export function CostSheetWorkflow({ sheet, locked, onSaved, onEditMaterial }) {
  const { toast } = useToast();
  const [details, setDetails] = React.useState(null);
  const [info, setInfo] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    let cancelled = false;
    setDetails(null);
    Promise.all(Object.keys(checks).map(async (stage) => {
      const result = await supabase.rpc('mfg_workflow_issues', { p_sheet: sheet.id, p_stage: stage });
      if (result.error) throw result.error;
      return [stage, result.data || []];
    })).then((values) => { if (!cancelled) setDetails(Object.fromEntries(values)); })
      .catch(() => { if (!cancelled) toast({ title: 'Error', description: 'No se pudieron cargar las comprobaciones automáticas', variant: 'destructive' }); });
    return () => { cancelled = true; };
  }, [sheet, toast]);
  const save = async (stage, key, field, value) => {
    setBusy(true);
    try {
      const { error } = await supabase.rpc('mfg_save_workflow_check', { p_sheet: sheet.id, p_stage: stage, p_key: key, p_field: field, p_value: value });
      if (error) throw error;
      await onSaved();
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'No se pudo guardar el check', variant: 'destructive' });
    } finally { setBusy(false); }
  };
  return <div className="mt-4 grid gap-3 lg:grid-cols-3">
    {MFG_COST_SHEET_STAGES.filter((stage) => checks[stage.id]).map((stage) => {
      const meta = stageStateMeta(sheet[stage.column]);
      const rows = [...checks[stage.id].map(([key, label]) => ({ key, label, manual: true,
        passed: sheet.workflow_checks?.[stage.id]?.[key]?.checked === true }))];
      rows.push({ key: 'automatic', label: 'Procesos automáticos', passed: details?.[stage.id]?.every((row) => row.passed === true) });
      return <section key={stage.id} className="rounded-lg border bg-white p-3">
        <h4 className="font-semibold">{stage.label} <span className={`ml-1 rounded border px-2 text-xs ${meta.className}`}>{meta.label}</span></h4>
        {rows.map((row) => <div key={row.key} className="mt-3 border-t pt-2">
          <div className="flex items-center gap-2 text-sm">
            {row.manual ? <input type="checkbox" className="accent-blue-600" aria-label={`${stage.label}: ${row.label}`} checked={Boolean(row.passed)}
              disabled={locked || busy} onChange={(event) => save(stage.id, row.key, 'checked', event.target.checked)} /> :
              <span role="checkbox" aria-checked={Boolean(row.passed)} aria-readonly="true" aria-label={`${stage.label}: ${row.label}`}
                className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border text-xs ${row.passed ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-400 bg-white'}`}>{row.passed ? '✓' : ''}</span>}
            <span>{row.label}{row.key === 'automatic' && !details ? ' · pendiente de comprobación' : ''}</span>
            {row.key === 'automatic' && <button type="button" className="text-blue-700 underline" onClick={() => setInfo(stage)}>Info</button>}
          </div>
          {row.key === 'shrinkage' && <Observation label={`${stage.label}: ${row.label}`} value={sheet.workflow_checks?.[stage.id]?.[row.key]?.notes}
            disabled={locked} save={(value) => save(stage.id, row.key, 'notes', value)} />}
        </div>)}
      </section>;
    })}
    <Dialog open={Boolean(info)} onOpenChange={(open) => { if (!open) setInfo(null); }}>
      <DialogContent className="max-h-[80vh] overflow-y-auto"><DialogHeader>
        <DialogTitle>Comprobaciones automáticas · {info?.label}</DialogTitle>
        <DialogDescription>Se revisan los procesos asignados a este estado en el catálogo. Precio exige precio del material mayor que cero, proveedor y referencia. Pulsa un material para editarlo. Consumo exige cantidad de material mayor que cero. Se comprueba también la ubicación del patrón en Patronaje y los documentos exigidos por cada proceso, por cantidad o por requisito específico asignado al estado.</DialogDescription>
      </DialogHeader>
        {!details ? <p>No se han cargado las comprobaciones.</p> : !details[info?.id]?.length ? <p>No hay controles de procesos asignados a este estado. Este check no bloquea la finalización.</p> :
          <ul className="space-y-2">{details[info?.id]?.map((row, index) => <li key={index} className={row.passed ? 'text-blue-700' : 'text-amber-800'}>{row.passed ? '✓' : 'Pendiente:'} {row.material_line_id && onEditMaterial ? <button type="button" className="text-left underline disabled:no-underline disabled:cursor-default" disabled={locked} onClick={() => { setInfo(null); onEditMaterial(row.material_line_id); }}>{row.label}</button> : row.label}</li>)}</ul>}
      </DialogContent>
    </Dialog>
  </div>;
}
